/**
 * GiveGiftForm.js
 *
 * Extracted form component from GiveGift.js for use inside modals.
 * Accepts onSuccess and onClose callbacks instead of navigating away.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SERVER_URL } from '../config';
import { MIN_DONATION_AMOUNT, PAYPAL_PERCENTAGE_FEE, PAYPAL_FIXED_FEE } from '../utils/constants';
import { useAuth } from '../AuthContext';
import PayPalCheckout from './PayPalCheckout';

function GiveGiftForm({ onSuccess, onClose, initialRecipient, initialEmail, initialPostId, initialMessage }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [giverName, setGiverName] = useState('');
  const [giverEmail, setGiverEmail] = useState('');
  const [recipient, setRecipient] = useState('');
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [postId, setPostId] = useState(null);
  const [selectedTip, setSelectedTip] = useState(null); // null = auto (first preset), number = explicit
  const [customTip, setCustomTip] = useState(''); // Raw input for custom amount
  const [isCustomTip, setIsCustomTip] = useState(false);
  const [recipientStatus, setRecipientStatus] = useState(null); // null, 'checking', 'exists', 'new'
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [recipientLocked, setRecipientLocked] = useState(false); // True if recipient pre-filled from URL
  const [recipientMerchantId, setRecipientMerchantId] = useState(null);

  const giftAmount = parseFloat(amount || 0);

  // Compute distinct tip presets: 2%, 5%, 10% of gift, min $1, rounded to nearest $1, deduplicated.
  // Fall back to [1, 2, 3] if percentages don't produce 3 distinct values (e.g. small gift amounts).
  const tipPresets = (() => {
    if (giftAmount <= 0) return [1, 2, 3];
    const pcts = [...new Set([0.02, 0.05, 0.10].map(pct => Math.max(1, Math.round(giftAmount * pct))))];
    return pcts.length === 3 ? pcts : [1, 2, 3];
  })();

  // When no explicit selection, default to first preset (if valid amount), else 0
  const tip = isCustomTip ? (parseFloat(customTip) || 0)
    : selectedTip !== null ? selectedTip
    : giftAmount >= MIN_DONATION_AMOUNT ? tipPresets[0]
    : 0;

  // Fee calculation — donor always covers fees (gross-up) so recipient nets exactly giftAmount.
  // Tip goes to platform; platform absorbs PayPal's cut on the tip portion.
  //   amountToSend = (giftAmount + PAYPAL_FIXED_FEE + tip) / (1 - PAYPAL_PERCENTAGE_FEE)
  const amountToSend = (() => {
    if (giftAmount <= 0) return 0;
    const ideal = (giftAmount + PAYPAL_FIXED_FEE + tip) / (1 - PAYPAL_PERCENTAGE_FEE);
    let amt = Math.floor(ideal * 100) / 100;
    for (let i = 0; i < 3; i++) {
      const paypalFee = Math.floor((PAYPAL_PERCENTAGE_FEE * amt + PAYPAL_FIXED_FEE) * 100) / 100;
      if (Math.round((amt - paypalFee - tip) * 100) / 100 >= giftAmount) break;
      amt = Math.round((amt + 0.01) * 100) / 100;
    }
    return amt;
  })();
  // Derive processing fee as the remainder so the order summary always adds up exactly.
  const processingFee = giftAmount > 0
    ? Math.round((amountToSend - giftAmount - tip) * 100) / 100
    : 0;

  // Pre-fill giver info if authenticated
  useEffect(() => {
    if (!authLoading && user) {
      setGiverName(user.username);
      setGiverEmail(user.email);
    }
  }, [authLoading, user]);

  // Check recipient status when email changes
  const checkRecipientStatus = async emailToCheck => {
    if (!emailToCheck || !emailToCheck.includes('@')) {
      setRecipientStatus(null);
      setRecipientInfo(null);
      return;
    }

    setRecipientStatus('checking');
    try {
      const res = await fetch(
        `${SERVER_URL}/paypal/users/email/${encodeURIComponent(emailToCheck)}/status`
      );
      const data = await res.json();

      if (data.user_exists) {
        setRecipientStatus('exists');
        setRecipientInfo(data);
        setRecipientMerchantId(data.merchant_id || null);
        // Auto-fill recipient name if user exists
        if (data.user_name) {
          setRecipient(data.user_name);
        }
      } else {
        setRecipientStatus('new');
        setRecipientInfo(null);
        setRecipientMerchantId(null);
        setRecipient(''); // Clear name for new users
      }
    } catch (err) {
      console.error('Error checking recipient status:', err);
      setRecipientStatus(null);
      setRecipientInfo(null);
    }
  };

  // Fetch pool details to get beneficiary information
  const fetchPoolDetails = useCallback(async poolId => {
    try {
      const res = await fetch(`${SERVER_URL}/posts/${poolId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.beneficiary_name) setRecipient(data.beneficiary_name);
        if (data.beneficiary_email) {
          setEmail(data.beneficiary_email);
          checkRecipientStatus(data.beneficiary_email);
        }
      }
    } catch (err) {
      console.error('Error fetching pool details:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill recipient info from URL parameters or initial props (modal usage)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const recipientParam = params.get('recipient') || initialRecipient;
    const emailParam = params.get('email') || initialEmail;
    const amountParam = params.get('amount');
    const messageParam = params.get('message') || initialMessage;
    const postIdParam = params.get('post_id') || initialPostId;

    if (recipientParam) setRecipient(recipientParam);
    if (emailParam) {
      setEmail(emailParam);
      setRecipientLocked(true);
      checkRecipientStatus(emailParam);
    }
    if (amountParam) setAmount(amountParam);
    if (messageParam) setMessage(messageParam);
    if (postIdParam) {
      setPostId(postIdParam);
      // When contributing to a pool, recipient is predetermined
      // Fetch pool details to get beneficiary info if not already provided
      if (!recipientParam || !emailParam) {
        fetchPoolDetails(postIdParam);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, fetchPoolDetails]);

  // Debounced email check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (email) {
        checkRecipientStatus(email);
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [email]);

  // Re-check recipient status when page regains focus (in case they completed setup in another tab)
  useEffect(() => {
    const handleFocus = () => {
      if (email) {
        checkRecipientStatus(email);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [email]);

  // Don't render form if not authenticated (will redirect)
  const inputClass = 'w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow';
  const labelClass = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1';

  return (
    <div className="max-w-md mx-auto px-4">
      <div className="mb-6 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-8">
        <h1 className="font-display text-2xl font-bold mb-6">Send a Gift</h1>
        <div className="flex flex-col gap-4">

          {/* Recipient Email — only shown on standalone /send page */}
          {!recipientLocked && !postId && (
            <div>
              <label className={labelClass}>Recipient Email</label>
              <input
                type="email"
                placeholder="friend@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className={inputClass}
              />
            </div>
          )}

          {/* Recipient status badges */}
          {email && recipientStatus === 'checking' && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-600">Checking recipient status...</p>
            </div>
          )}
          {email && recipientStatus === 'exists' && recipientInfo && (
            <div className={`p-3 border rounded-lg ${recipientInfo.can_receive_payments ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Recipient:</span> {recipientInfo.user_name}
              </p>
              {recipientInfo.can_receive_payments ? (
                <p className="text-xs text-green-600 mt-1">✓ Account ready to receive gifts</p>
              ) : (
                <p className="text-xs text-red-600 mt-1">
                  ❌ {recipientInfo.user_name} hasn't connected PayPal yet. Ask them to go to <strong>Settings → Payments</strong> and click <strong>Connect PayPal</strong>.
                </p>
              )}
            </div>
          )}
          {email && recipientStatus === 'new' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Recipient not found:</span> {email}
              </p>
              <p className="text-xs text-red-600 mt-1">
                ❌ This person isn't on Gifted yet. Ask them to create a free account at gifted.com and connect PayPal under <strong>Settings → Payments</strong>.
              </p>
            </div>
          )}

          {/* Only show amount and other fields if recipient is ready OR if checking status */}
          {(!email || recipientStatus === 'checking' || (recipientStatus === 'exists' && recipientInfo?.can_receive_payments)) && (
            <>
              {/* Amount */}
              <div>
                <label className={labelClass}>Amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={amount}
                    onChange={e => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        setAmount(value);
                      }
                    }}
                    required
                    className={`${inputClass} pl-7`}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">All gifts are in US dollars (USD). If you're paying from a non-US account, PayPal will convert your local currency at checkout.</p>
                {amount && giftAmount > 0 && giftAmount < MIN_DONATION_AMOUNT && (
                  <p className="text-xs text-red-600 mt-1">Minimum gift is ${MIN_DONATION_AMOUNT}</p>
                )}
              </div>

              {/* Order Summary & Tip */}
              {amount && giftAmount >= MIN_DONATION_AMOUNT && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                  {/* Tip selector */}
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Add an optional gift to Gifted</div>
                    <p className="text-xs text-gray-400 mb-2">Your support helps us keep Gifted running.</p>
                    <div className="flex gap-2">
                      {tipPresets.map(preset => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => { setIsCustomTip(false); setCustomTip(''); setSelectedTip(tip === preset ? 0 : preset); }}
                          className={`flex-1 py-1.5 text-sm rounded-lg border transition-colors ${!isCustomTip && tip === preset && tip > 0 ? 'bg-indigo-100 text-indigo-700 border-indigo-400' : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'}`}
                        >
                          ${preset}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => { if (isCustomTip) { setIsCustomTip(false); setCustomTip(''); setSelectedTip(null); } else { setIsCustomTip(true); setCustomTip(''); setSelectedTip(null); } }}
                        className={`flex-1 py-1.5 text-sm rounded-lg border transition-colors ${isCustomTip ? 'bg-indigo-100 text-indigo-700 border-indigo-400' : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'}`}
                      >
                        Custom
                      </button>
                    </div>
                    {isCustomTip && (
                      <div className="relative mt-2">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="0.00"
                          value={customTip}
                          onChange={e => {
                            const val = e.target.value;
                            if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                              setCustomTip(val);
                            }
                          }}
                          className="w-full border border-indigo-400 rounded-lg pl-7 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          autoFocus
                        />
                        {(parseFloat(customTip) || 0) > giftAmount && (
                          <p className="text-xs text-amber-600 mt-1">Your tip is larger than the gift amount — thanks for the extra support!</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Order Summary */}
                  <div className="pt-3 border-t border-gray-300 space-y-1">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Order Summary</div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Gift to {recipient || 'recipient'}</span>
                      <span>${giftAmount.toFixed(2)}</span>
                    </div>
                    {tip > 0 && (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Optional gift to Gifted</span>
                        <span>${tip.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>PayPal processing fee</span>
                      <span>${processingFee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium text-gray-900 mt-2 pt-2 border-t border-gray-400">
                      <span>You pay</span>
                      <span>${amountToSend.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold text-green-700 mt-1">
                      <span>{recipient || 'Recipient'} receives</span>
                      <span>${giftAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Message */}
              <div>
                <label className={labelClass}>Message <span className="normal-case font-normal text-gray-400">(optional)</span></label>
                <textarea
                  placeholder="Write a personal note..."
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={3}
                  className={inputClass}
                />
              </div>

              {giftAmount >= MIN_DONATION_AMOUNT && recipientStatus === 'exists' && recipientInfo?.can_receive_payments ? (
                <PayPalCheckout
                  key={recipientMerchantId || 'no-merchant'}
                  amount={giftAmount}
                  tipAmount={tip}
                  recipientName={recipient}
                  recipientEmail={email}
                  recipientMerchantId={recipientMerchantId}
                  giverName={giverName}
                  giverEmail={giverEmail}
                  message={message}
                  postId={postId}
                  onSuccess={({ giftId, amount: paidAmount, fundingSource, buyerEmail }) => {
                    navigate('/payment-confirmation', {
                      state: { giftId, amount: paidAmount, fundingSource, buyerEmail },
                    });
                  }}
                  onError={(err) => setError(err.message || 'Payment failed. Please try again.')}
                />
              ) : (
                <button
                  type="button"
                  disabled
                  className="bg-indigo-500 text-white py-2.5 px-4 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {recipientStatus === 'checking' ? 'Verifying recipient...' :
                   !amount || giftAmount <= 0 ? 'Enter an amount to continue' :
                   giftAmount < MIN_DONATION_AMOUNT ? `Minimum gift is $${MIN_DONATION_AMOUNT}` :
                   'Proceed to Payment'}
                </button>
              )}
            </>
          )}

          {error && (
            <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GiveGiftForm;
